/**
 * Startseiten-Sektionen (Layout Oktober 2026). Werden über PageRenderer anhand von section.type gerendert,
 * Inhalte kommen aus dem Page-Builder (backend/ops/pages.py). Bewusst ohne Scroll-Einblendungen.
 */
import OrderTrackingPromo from "@/components/common/OrderTrackingPromo";
import { PayOptionsSection } from "@/components/pay/PayOptions";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Crown, Phone, ShieldCheck, Wrench, Sparkles, MessageCircle, Play,
} from "lucide-react";
import { useCatalog, productImage } from "@/lib/catalog";
import { CatalogLoadState } from "@/components/common/CatalogLoadState";
import { PromoHeroBanner } from "@/components/common/PromoKit";
import { usePromotion } from "@/lib/promotion";
import { BestpreisHero } from "@/components/BestpreisWidgets";
import academyMedia from "@/lib/academy-media.json";
import { imgSet } from "@/lib/img";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => `${Number(n || 0).toLocaleString("de-DE", { maximumFractionDigits: 0 })} €`;

/** Niedrigster Paketpreis > 0 (= „ab"-Preis), sonst null. */
export function fromPrice(product) {
  const prices = (product?.packages || []).map((p) => Number(p.price_gross || 0)).filter((v) => v > 0);
  return prices.length ? Math.min(...prices) : null;
}

/** **fett** im Fließtext erlauben, kein HTML aus dem Backend. */
function Bold({ text = "" }) {
  return String(text).split("**").map((part, i) => (i % 2 ? <b key={i}>{part}</b> : <React.Fragment key={i}>{part}</React.Fragment>));
}

function CtaLink({ cta, className, dark = false }) {
  if (!cta?.label) return null;
  const href = cta.href || "#";
  const cls = className || (dark
    ? "inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white hover:bg-white/10 transition-colors text-sm font-medium"
    : "btn-secondary");
  return href.startsWith("http")
    ? <a href={href} target="_blank" rel="noreferrer" className={cls}>{cta.label}</a>
    : <Link to={href} className={cls}>{cta.label}</Link>;
}

function Header({ d, dark = false, center = false }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
      {d.eyebrow && (
        <div className={dark
          ? "inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-yellow/50 bg-brand-yellow/10 text-brand-yellow text-[11px] uppercase tracking-widest"
          : "pill-badge"}>
          {dark && <Crown className="w-3.5 h-3.5" />} {d.eyebrow}
        </div>
      )}
      {(d.title || d.title_accent) && (
        <h2 className={`font-heading text-4xl md:text-5xl leading-[1.05] mt-4 ${dark ? "text-white" : ""}`}>
          {d.title} {d.title_accent && <span className="font-serif-italic text-brand-yellow">{d.title_accent}</span>}
        </h2>
      )}
      {d.subtitle && <p className={`mt-4 text-lg ${dark ? "text-white/80" : "text-ink-soft"}`}>{d.subtitle}</p>}
    </div>
  );
}

// ---------- 2 · Aktion (nur sichtbar, solange die Aktion läuft) ----------
export function PromoSection() {
  const { active } = usePromotion();
  if (!active) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 md:pt-10" data-testid="home-promo">
      <PromoHeroBanner />
    </section>
  );
}

// ---------- 3 · Bestseller + Kategorien ----------
export function BestsellerSection({ section }) {
  const d = section.data || {};
  const { products, loading, error, retry } = useCatalog();
  const list = (d.product_slugs || []).map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const countBy = (cat) => products.filter((p) => p.category === cat).length;
  return (
    <div>
      <Header d={d} />
      <CatalogLoadState loading={loading} error={error} onRetry={retry} id={`home-bestseller-${section.id}`} />
      {!loading && !error && (
        <div className="mt-10 flex md:grid md:grid-cols-3 gap-5 overflow-x-auto snap-x snap-mandatory -mr-6 pr-6 md:mr-0 md:pr-0 pb-2" data-testid="home-bestseller-cards">
          {list.map((p) => {
            const price = fromPrice(p);
            const badge = (d.badges || {})[p.slug];
            return (
              <article key={p.slug} className="group card-clean overflow-hidden flex flex-col min-w-[80%] sm:min-w-[55%] md:min-w-0 snap-start" data-testid={`bestseller-${p.slug}`}>
                <Link to={`/produkte/${p.slug}`} className="relative block bg-cream aspect-square overflow-hidden">
                  <img {...imgSet(productImage(p), "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw")} alt={p.name} loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-[1.03]" />
                  {badge && <span className="absolute top-4 left-4 bg-ink text-white text-[10px] tracking-widest uppercase font-semibold px-3 py-1 rounded-full">{badge}</span>}
                </Link>
                <div className="p-6 flex flex-col gap-2 flex-1">
                  <span className="text-[11px] tracking-widest uppercase text-ink-muted font-semibold">{p.category_label}</span>
                  <Link to={`/produkte/${p.slug}`}><h3 className="font-heading text-2xl md:text-3xl text-ink leading-tight">{p.name}</h3></Link>
                  <p className="text-sm text-ink-soft leading-relaxed">{p.positioning || p.short}</p>
                  {price && <div className="mt-1 font-heading text-3xl text-brand-yellow"><span className="text-xs text-ink-muted font-body align-middle mr-1">ab</span>{fmtEur(price)}</div>}
                  <div className="mt-auto pt-4 flex flex-wrap gap-2">
                    <Link to={`/produkte/${p.slug}`} className="btn-accent !py-2.5 !px-5" data-testid={`bestseller-view-${p.slug}`}>Ansehen</Link>
                    <Link to="/vergleich" className="btn-secondary !py-2.5 !px-5">Vergleichen</Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {(d.categories || []).length > 0 && (
        <>
          <div className="mt-12 text-xs tracking-widest uppercase text-ink-muted font-semibold">{d.categories_title || "Oder direkt nach Kategorie stöbern"}</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="home-category-tiles">
            {d.categories.map((c) => {
              const imgProduct = c.image_slug && products.find((p) => p.slug === c.image_slug);
              const img = c.image_url || (imgProduct ? productImage(imgProduct) : "");
              const n = c.cat ? countBy(c.cat) : null;
              return (
                <Link key={c.label} to={c.href || "/fotoboxen"} className="rounded-2xl border border-hair bg-cream hover:border-brand-yellow/50 transition-colors p-3 md:p-4 flex items-center gap-3">
                  {img && <img {...imgSet(img, "64px")} alt="" width="64" height="64" loading="lazy" className="w-12 h-12 md:w-16 md:h-16 object-contain shrink-0" />}
                  <span className="min-w-0">
                    <span className="block font-heading text-base md:text-lg text-ink leading-tight">{c.label}</span>
                    <span className="block text-xs md:text-sm text-ink-muted">{c.subtitle || (n ? `${n} Modelle` : "")} →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------- 4 · Fotobox Academy Pro ----------
export function AcademySection({ section }) {
  const d = section.data || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 items-center" data-testid="home-academy">
      <div>
        <Header d={{ eyebrow: d.eyebrow, title: d.title, title_accent: d.title_accent }} dark />
        {d.text && <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl"><Bold text={d.text} /></p>}
        {(d.stats || []).length > 0 && (
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
            {d.stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                <div className="font-heading text-3xl text-brand-yellow leading-none">{s.value}</div>
                <div className="mt-1 text-xs text-white/75">{s.label}</div>
              </div>
            ))}
          </div>
        )}
        {(d.tools || []).length > 0 && (
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/85">
            {d.tools.map((t) => <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-brand-yellow mt-0.5 shrink-0" strokeWidth={3} />{t}</li>)}
          </ul>
        )}
        <div className="mt-7 flex flex-wrap gap-3">
          {d.primary_cta?.label && <Link to={d.primary_cta.href || "/fotobox-pro-academy"} className="btn-accent" data-testid="home-academy-cta">{d.primary_cta.label} <ArrowRight className="w-4 h-4" /></Link>}
          <CtaLink cta={d.secondary_cta} dark />
        </div>
        {d.note && <p className="mt-4 text-xs text-white/60 leading-relaxed" data-testid="home-academy-status">{d.note}</p>}
      </div>
      <Link to={d.primary_cta?.href || "/fotobox-pro-academy"} className="block rounded-3xl overflow-hidden border border-white/10 bg-white/5 p-3 md:p-4">
        <img {...imgSet(`${BACKEND}${academyMedia.overview.src}`, "(max-width: 768px) 100vw, 50vw")} width={academyMedia.overview.width} height={academyMedia.overview.height}
          alt={academyMedia.overview.alt} loading="lazy" decoding="async" className="block w-full h-auto rounded-2xl" />
        {d.image_caption && <p className="mt-3 text-xs text-white/60">{d.image_caption}</p>}
      </Link>
    </div>
  );
}

// ---------- 6 · Ein Spiegel. Viele Looks. (Rahmenwechsel) ----------
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
}

export function LooksSection({ section }) {
  const d = section.data || {};
  const frames = d.frames || [];
  const hold = Number(d.interval_ms || 2500);
  const fade = Number(d.fade_ms || 800);
  const reduce = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (reduce || frames.length < 2) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") setIdx((i) => (i + 1) % frames.length);
    }, hold + fade);
    return () => clearInterval(t);
  }, [reduce, frames.length, hold, fade]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center" data-testid="home-looks">
      <div className="relative" aria-label="Magic Mirror mit wechselnden Barockrahmen">
        {/* Alle Bilder liegen deckungsgleich übereinander; nur die Deckkraft wechselt. */}
        <div className="relative w-full" style={{ aspectRatio: d.aspect_ratio || "1200 / 1107" }}>
          {frames.map((f, i) => (
            <img key={f.image} {...imgSet(f.image, "(max-width: 768px) 100vw, 50vw")} alt={i === idx ? `Magic Mirror mit Rahmen ${f.name}` : ""} aria-hidden={i !== idx}
              loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ opacity: i === idx ? 1 : 0, transition: `opacity ${fade}ms ease-in-out` }} />
          ))}
        </div>
      </div>
      <div>
        <Header d={{ eyebrow: d.eyebrow, title: d.title, title_accent: d.title_accent }} />
        {d.text && <p className="mt-5 text-lg text-ink-soft leading-relaxed">{d.text}</p>}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="home-looks-names">
          {frames.map((f, i) => (
            <Link key={f.name} to={f.href || "/zubehoer/magic-mirror-rahmen"}
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${i === idx ? "bg-ink text-white border-ink" : "border-hair text-ink-soft hover:border-ink/40"}`}>
              {f.name}
            </Link>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          {d.primary_cta?.label && <Link to={d.primary_cta.href || "/zubehoer/magic-mirror-rahmen"} className="btn-accent">{d.primary_cta.label} <ArrowRight className="w-4 h-4" /></Link>}
          <CtaLink cta={d.secondary_cta} />
        </div>
        {d.price_note && <p className="mt-4 text-sm text-ink-muted">{d.price_note}</p>}
      </div>
    </div>
  );
}

// ---------- 7 · Fotoautomaten ----------
export function AutomatenSection({ section }) {
  const d = section.data || {};
  const { products, loading, error, retry } = useCatalog();
  const list = (d.product_slugs || []).map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  return (
    <div data-testid="home-automaten">
      <Header d={d} dark />
      {(d.audiences || []).length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {d.audiences.map((a) => <span key={a} className="text-xs text-white/80 border border-white/20 rounded-full px-3 py-1">{a}</span>)}
        </div>
      )}
      <CatalogLoadState loading={loading} error={error} onRetry={retry} id={`home-automaten-${section.id}`} />
      {!loading && !error && (
        <div className="mt-10 flex md:grid md:grid-cols-3 gap-5 overflow-x-auto snap-x snap-mandatory -mr-6 pr-6 md:mr-0 md:pr-0 pb-2">
          {list.map((p) => {
            const price = fromPrice(p);
            return (
              <Link key={p.slug} to={`/produkte/${p.slug}`} className="group rounded-3xl border border-white/10 bg-white/5 hover:bg-white/[0.08] transition-colors overflow-hidden flex flex-col min-w-[80%] sm:min-w-[55%] md:min-w-0 snap-start" data-testid={`automat-${p.slug}`}>
                <div className="relative aspect-square overflow-hidden" style={{ background: "radial-gradient(60% 60% at 50% 55%, rgba(244,184,74,0.18) 0%, transparent 70%)" }}>
                  <img {...imgSet(productImage(p), "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw")} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
                <div className="p-6 flex flex-col gap-2 flex-1">
                  {(d.labels || {})[p.slug] && <span className="text-[11px] tracking-widest uppercase text-white/50 font-semibold">{d.labels[p.slug]}</span>}
                  <h3 className="font-heading text-2xl text-white leading-tight">{p.name}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{p.short || p.positioning}</p>
                  <ul className="mt-1 space-y-1 text-sm text-white/80">
                    {(p.highlights || []).slice(0, 2).map((h) => <li key={h} className="flex items-start gap-2"><Check className="w-4 h-4 text-brand-yellow mt-0.5 shrink-0" strokeWidth={3} />{h}</li>)}
                  </ul>
                  <div className="mt-auto pt-3 flex items-end justify-between">
                    {price && <span className="font-heading text-3xl text-brand-yellow">{fmtEur(price)}</span>}
                    <span className="text-sm text-white/80 inline-flex items-center gap-1">Details <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {(d.box_title || d.box_text) && (
        <div className="mt-8 rounded-2xl border border-brand-yellow/30 bg-brand-yellow/[0.06] p-5 md:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            {d.box_title && <div className="font-semibold text-white text-lg">{d.box_title}</div>}
            {d.box_text && <div className="text-sm text-white/70 mt-1">{d.box_text}</div>}
          </div>
          <div className="flex flex-wrap gap-3">
            {d.primary_cta?.label && <Link to={d.primary_cta.href || "/showroom"} className="btn-accent">{d.primary_cta.label}</Link>}
            <CtaLink cta={d.secondary_cta} dark />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- 8 · Warum bei uns + Showroom-Video ----------
const TRUST_ICONS = { phone: Phone, check: Check, shield: ShieldCheck, wrench: Wrench, sparkles: Sparkles, message: MessageCircle };

function ShowroomVideo({ v }) {
  const [playing, setPlaying] = useState(false);
  const src = v.url?.startsWith("/api/") ? `${BACKEND}${v.url}` : v.url;
  const poster = v.poster?.startsWith("/api/") ? `${BACKEND}${v.poster}` : v.poster;
  return (
    <div className="contrast-dark rounded-3xl overflow-hidden bg-ink text-white flex flex-col h-full" data-testid="home-showroom-video">
      <div className="relative bg-black">
        {playing ? (
          <video src={src} poster={poster} controls autoPlay playsInline preload="metadata" width="720" height="1280"
            className="block w-full h-[420px] md:h-[440px] object-contain bg-black" data-testid="home-showroom-video-player">
            Ihr Browser unterstützt die Videowiedergabe nicht.
          </video>
        ) : (
          <button type="button" onClick={() => setPlaying(true)} className="relative block w-full h-[420px] md:h-[440px] group" aria-label={`Video abspielen: ${v.title || "Showroom"}`}>
            {poster && <img {...imgSet(poster, "(max-width: 768px) 100vw, 50vw")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" loading="lazy" />}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-white/90 text-ink flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform"><Play className="w-7 h-7 ml-1" fill="currentColor" /></span>
            </span>
          </button>
        )}
      </div>
      <div className="p-6 flex flex-col gap-2">
        {v.title && <div className="font-heading text-2xl text-white">{v.title}</div>}
        {v.text && <p className="text-sm text-white/75">{v.text}</p>}
        {v.cta_label && <div className="pt-2"><Link to={v.cta_href || "/showroom"} className="btn-accent !py-2.5 !px-5">{v.cta_label}</Link></div>}
      </div>
    </div>
  );
}

export function TrustVideoSection({ section }) {
  const d = section.data || {};
  return (
    <div>
      <Header d={d} />
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6 items-stretch">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:auto-rows-fr">
          {(d.items || []).map((it) => {
            const Icon = TRUST_ICONS[it.icon] || Check;
            return (
              <div key={it.title} className="card-clean p-5">
                <div className="w-10 h-10 rounded-full bg-brand-yellow/15 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-brand-yellow" strokeWidth={2.2} /></div>
                <div className="font-heading text-lg text-ink leading-tight">{it.title}</div>
                {it.text && <div className="text-sm text-ink-soft mt-1.5">{it.text}</div>}
              </div>
            );
          })}
        </div>
        {d.video?.url && <ShowroomVideo v={d.video} />}
      </div>
    </div>
  );
}

// ---------- 9 · Bestpreisgarantie ----------
export function BestpreisSection() {
  return <BestpreisHero />;
}

function OrderTrackingSection() {
  return <OrderTrackingPromo testid="home-order-tracking" />;
}

export const HOME_RENDERERS = {
  promo: PromoSection,
  bestseller: BestsellerSection,
  academy: AcademySection,
  looks: LooksSection,
  automaten: AutomatenSection,
  trust_video: TrustVideoSection,
  bestpreis: BestpreisSection,
  order_tracking: OrderTrackingSection,
  payment_options: () => <PayOptionsSection zu />,
};

// Diese Typen bringen ihren eigenen Rahmen mit (kein Standard-Sektions-Wrapper mit Rand/Abstand).
export const BARE_TYPES = new Set(["promo", "order_tracking", "payment_options"]);
